// @flow
import React, { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import InputQuantity from '../InputQuantity/InputQuantity';
import Cleave from 'cleave.js/react';
import { LoadingContentAvatar, LoadingContentStripe } from '../LoadingContentFigures/LoadingContentFigures';
import Loading from '../Loading/Loading';
import InputPrice from '../InputPrice/InputPrice';
import { CommaFormat } from '../Price/Price';
import hash from 'object-hash';
import sr from 'seedrandom';

import icons from '../../assets/styles/icons.scss';
import formStyle from '../../assets/styles/form.scss'
import style from './Product.scss';
import boxStyle from '../../assets/styles/box.scss';
import loadingStripeStyle from '../../elements/LoadingContentFigures/LoadingContentFigures.scss';

import type { InputQuantityAction } from '../InputQuantity/InputQuantity';
import type { Category, ItemState, OnlineStore, OrderItem, ShoppingCartItem, BagItem } from '../../entities';
import type { Element, Node } from 'react';
import type { LocaleState } from '../../modules/locale/duck';
import type { ProductType } from "../SingleProduct/SingleProduct";

export type UserType = 'purchaser' | 'traveler';

import baggageXS from '../../assets/img/flight/volumetric/baggage/01.png';
import baggageS from '../../assets/img/flight/volumetric/baggage/02.png';
import baggageM from '../../assets/img/flight/volumetric/baggage/03.png';
import baggageL from '../../assets/img/flight/volumetric/baggage/04.png';
import cameraXS from '../../assets/img/flight/volumetric/camera/01.png';
import cameraS from '../../assets/img/flight/volumetric/camera/02.png';
import cameraM from '../../assets/img/flight/volumetric/camera/03.png';
import cameraL from '../../assets/img/flight/volumetric/camera/04.png';
import makeupXS from '../../assets/img/flight/volumetric/makeup/01.png';
import makeupS from '../../assets/img/flight/volumetric/makeup/02.png';
import makeupM from '../../assets/img/flight/volumetric/makeup/03.png';
import makeupL from '../../assets/img/flight/volumetric/makeup/04.png';
import shoesXS from '../../assets/img/flight/volumetric/shoes/01.png';
import shoesS from '../../assets/img/flight/volumetric/shoes/02.png';
import shoesM from '../../assets/img/flight/volumetric/shoes/03.png';
import shoesL from '../../assets/img/flight/volumetric/shoes/04.png';
import supplementsXS from '../../assets/img/flight/volumetric/supplements/01.png';
import supplementsS from '../../assets/img/flight/volumetric/supplements/02.png';
import supplementsM from '../../assets/img/flight/volumetric/supplements/03.png';

export type Props = {
  index: number;
  localeState: LocaleState;
  categoriesMap?: { [key: string]: Category };
  item: OrderItem | BagItem | ShoppingCartItem;
  open: boolean;
  isLoading: boolean;
  showVolumeTier?: boolean;
  type?: ProductType;
  handleOpen: (index: number) => void;
  handleChangeAmount?: (action: InputQuantityAction, itemNumber: number, index: number) => void;
  handleChangeShipping?: (e: Event, itemNumber: number, index: number) => void;
  handleBlurCleave?: (e: Event, itemNumber: number, index: number) => void;
  handleEdit?: () => void;
  handleDelete?: (itemNumber: number, index: number) => void;
  hideDelete?: boolean;
  handleShippingInit?: (cleave: Cleave, itemNumber: number, index: number) => void;
  shipping?: {
    value: string;
    errors?: string[];
  };
  amount?: {
    value: number;
    errors?: string[];
  };
  onlineStore: OnlineStore;
  renderContent?: (props: Props) => Node;
  subtitle?: string;
  userType: UserType;
  handleRef?: (ref: any) => void;
};

const messages = defineMessages({
  estimatedKgWeight: {
    id: 'product.weight.kg',
  },
  estimatedGWeight: {
    id: 'product.weight.g',
  },
  NOT_IN_TIME: {
    id: 'product.cancelationMotive.notInTime',
  },
  VENDOR_CANCELLED: {
    id: 'product.cancelationMotive.vendorCanceled',
  },
  PACKAGE_TOO_BIG: {
    id: 'product.cancelationMotive.packageTooBig',
  },
  PACKAGE_TOO_HEAVY: {
    id: 'product.cancelationMotive.packageTooHeavy',
  },
});

const volumeImages = {
  '4': {
    'XS': cameraXS,
    'S': cameraS,
    'M': cameraM,
    'L': cameraL,
  },
  '6': {
    'XS': supplementsXS,
    'S': supplementsS,
    'M': supplementsM,
  },
  '12': {
    'XS': shoesXS,
    'S': shoesS,
    'M': shoesM,
    'L': shoesL,
  },
  '13': {
    'XS': makeupXS,
    'S': makeupS,
    'M': makeupM,
    'L': makeupL,
  },
  '9': {
    'XS': baggageXS,
    'S': baggageS,
    'M': baggageM,
    'L': baggageL,
  },
};

export const ProductLoading = () => (
  <section className={classNames(boxStyle.box, style.orderWrapper)}>
    <section className={style.loadingWrapper}>
      <LoadingContentAvatar colorClassName={loadingStripeStyle.black200}/>
      <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
    </section>
  </section>
);

export default class Product extends Component<Props, void> {
  buildCancelationMessage() {
    const { item } = this.props;
    if (item && item.cancellation && item.cancellation.motive) {
      return item.cancellation.motive.name !== 'OTHER' ?
        this.context.intl.formatMessage(messages[item.cancellation.motive.name]) :
        item.cancellation.message;
    }
  }

  buildShippingAndTaxes = (): Element<'article'> => {
    const { item } = this.props;
    let sum = 0;
    const shippingParsed = parseFloat(item.shipping);
    const taxesParsed = parseFloat(item.tax);

    const preSum = isNaN(shippingParsed) ? sum : shippingParsed;
    sum = isNaN(taxesParsed) ? preSum : preSum + taxesParsed;

    return (
      <article className={formStyle.col}>
        <FormattedMessage id="product.shipping.label">{
          txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
        }</FormattedMessage>
        <p>{sum > 0 ? `$ ${CommaFormat({ price: sum.toFixed(2) })}` :
          this.context.intl.formatMessage({ id: 'product.shipping.free' })}</p>
      </article>);
  };

  getVolumeImage = (categoryId: string, volumeTier: string): string => {
    return volumeImages[categoryId][volumeTier];
  };

  buildFirstRow() {
    const { item, isLoading, type, userType, handleChangeAmount, amount, index } = this.props;
    const total = (parseFloat(item.price) * parseFloat(item.amount)).toFixed(2);
    let content;
    if (type === 'EDIT') {
      content = (
        <Fragment>
          {/*price*/}
          <article className={formStyle.col}>
            <FormattedMessage id="product.price.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: item.price })}</p>
          </article>
          {/*total*/}
          <article className={classNames(formStyle.col, style.desktop)}>
            <FormattedMessage id="product.total">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: total })}</p>
          </article>
          {/*quantity*/}
          <article className={formStyle.col}>
            <FormattedMessage id="product.amount.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            {amount && handleChangeAmount && <InputQuantity disableLess={amount.value === 1}
                                                            disableMore={amount.value === 5}
                                                            quantity={`${amount.value}`}
                                                            handleChange={(action: InputQuantityAction) =>
                                                              handleChangeAmount(action, item.number, index)}/>}
          </article>
        </Fragment>);
    } else {
      content = (
        <Fragment>
          {/*price*/}
          <article className={formStyle.col}>
            <FormattedMessage id="product.price.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: item.price })}</p>
          </article>
          {/*total*/}
          <article className={classNames(formStyle.col, style.desktop)}>
            <FormattedMessage id="product.total">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: total })}</p>
          </article>
          {/*amount-text*/}
          <article className={formStyle.col}>
            <FormattedMessage id="product.amount.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>{item.amount}</p>
          </article>
          {/*shipping or Shipping and Taxes*/}
          {/*userType === 'purchaser' ? (
            <article className={formStyle.col}>
              <FormattedMessage id="product.shipping.label">{
                txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
              }</FormattedMessage>
              <p>{item.shipping !== '0.00' ? `$ ${CommaFormat({ price: item.shipping })}` :
                this.context.intl.formatMessage({ id: 'product.shipping.free' })}</p>
            </article>) : this.buildShippingAndTaxes()*/}
          {<article className={formStyle.col}>
            <FormattedMessage id="product.shipping.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>{item.shipping !== '0.00' ? `$ ${CommaFormat({ price: item.shipping })}` :
              this.context.intl.formatMessage({ id: 'product.shipping.free' })}</p>
          </article>}
        </Fragment>);
    }
    return (
      <section className={classNames(formStyle.row, formStyle.dualWrap, style.row,
        { [style.disabled]: isLoading })}>
        {content}
      </section>
    );
  }

  buildSecondRow() {
    const { item, isLoading, type, localeState, categoriesMap, userType } = this.props;
    const total = (parseFloat(item.price) * parseFloat(item.amount)).toFixed(2);
    let content;
    /*const showOriginalPackaging = !!categoriesMap && !!categoriesMap[`${item.itemCategoryId}`] &&
      categoriesMap[`${item.itemCategoryId}`].originalPackagingSelectable;*/
    const showOriginalPackaging = userType === 'traveler';
    if (type === 'EDIT') {
      content = (
        <Fragment>
          {/*category*/}
          <article className={classNames(formStyle.col, style.desktop, style.twoColSpan)}>
            <FormattedMessage id="product.category.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            {categoriesMap &&
            <p>{categoriesMap[`${item.itemCategoryId}`].translations[localeState.locale.toUpperCase()].name}</p>}
          </article>
          {/*total*/}
          <article className={classNames(formStyle.col, style.mobile)}>
            <FormattedMessage id="product.total">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: total })}</p>
          </article>
          {/*originalPackaging*/}
          {showOriginalPackaging && <article className={formStyle.col}>
            <FormattedMessage id="product.packaging.label">{
              txt => (<label className={classNames(formStyle.label, style.label, style.col)}>{txt}</label>)
            }</FormattedMessage>
            <FormattedMessage
              id={item.originalPackaging ? 'product.packaging.withBox' : 'product.packaging.withoutBox'}>{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </article>}
        </Fragment>
      );
    } else {
      content = (
        <Fragment>
          {/*dummy*/}
          <article className={classNames(formStyle.col, style.desktop)}>
            {item.itemCategoryId && categoriesMap && <Fragment>
              <FormattedMessage id="product.category.label">{
                txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
              }</FormattedMessage>
              <p>{categoriesMap[`${item.itemCategoryId}`].translations[localeState.locale.toUpperCase()].name}</p>
            </Fragment>}
          </article>
          {/*total*/}
          <article className={classNames(formStyle.col, style.mobile)}>
            <FormattedMessage id="product.total">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>$ {CommaFormat({ price: total })}</p>
          </article>
          {/*originalPackaging*/}
          {showOriginalPackaging && <article className={classNames(formStyle.col, style.col)}>
            <FormattedMessage id="product.packaging.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <FormattedMessage
              id={item.originalPackaging ? 'product.packaging.withBox' : 'product.packaging.withoutBox'}>{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </article>}
          {/*weight*/}
          {/*<article className={formStyle.col}>
            <FormattedMessage id="product.weight.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>{item.weight} Kg</p>
          </article>*/}
        </Fragment>
      );
    }
    return (
      <section className={classNames(formStyle.row, formStyle.dualWrap, style.row,
        { [style.disabled]: isLoading })}>
        {content}
      </section>
    );
  }

  buildVolumeBlock() {
    const { item, type } = this.props;
    return item.volumeTier && item.volumeTier.code !== 'NA' && type !== 'EDIT' && <section className={style.volume}>
      <FormattedMessage id="product.volume">{
        txt => <p className={style.label}>{txt}</p>
      }</FormattedMessage>
      {[4, 6, 12, 13].includes(item.itemCategoryId) ? (
        <img src={this.getVolumeImage(item.itemCategoryId.toString(), item.volumeTier.code)} alt="Qempo"/>) : (
        <img className={style.baggage} src={this.getVolumeImage('9', item.volumeTier.code)} alt="Qempo"/>)}
    </section>;
  }
  
  buildUrl = (url: string): string => {
    const {userType} = this.props;
    let rUrl = url;

    // if purchaser remove all query params
    if (userType === 'purchaser') {
      if (URL && url.includes('amazon')) {
        const u = new URL(url);
        rUrl = `${u.origin}${u.pathname}`;
      }
    }
    
    /*if (userType === 'traveler') {
      if (URL && url.includes('amazon')) {
        const u = new URL(url);
        const hn = hash(url);
        const p = sr(hn)();
        // const p = Math.random();
        if (p >= 0.50) {
          u.searchParams.set('tag', 'jevedel-20');
        }else {
          u.searchParams.set('tag', 'arturogoga-20');
        }
        u.searchParams.set('linkCode', 'osi');
        u.searchParams.set('th', '1');
        u.searchParams.set('psc', '1');
        if (rUrl.toString) {
          rUrl = u.toString();
        }
      }
    }*/
    
    return rUrl;
  }

  render() {
    const {
      item, open, type, handleOpen, handleEdit, handleDelete, isLoading, hideDelete, localeState, categoriesMap,
      handleShippingInit, handleChangeShipping, shipping, amount, onlineStore, handleBlurCleave, index, renderContent,
      subtitle, showVolumeTier = false, handleRef,
    } = this.props;
    // const { orderItem, externalInvoice, tracking } = item;
    const product = /*orderItem ? orderItem : */item;
    const cancelationMessage = this.buildCancelationMessage();
    return (
      <section className={classNames(style.orderWrapper, boxStyle.box, boxStyle.clickable)}>
        <button className={classNames(style.buttonWrapper)}
                onClick={() => handleOpen(index)}>
          <div className={style.imageWrapper}>
            {!!type &&
            <div className={classNames(style.type, style[type.toLowerCase()])}>
              {type === 'CANCELED' &&
              <i className={icons.iconCloseMedium}/>}
              {type !== 'CANCELED' && <p>{product.amount}</p>}
            </div>}
            <img className={style.itemImage}
                 src={product.image || (product: any).imageURI} alt="Order image"/>
          </div>
          <section className={style.title}>
            <p className={style.name}>{product.name}</p>
            {type && subtitle && type !== 'EDIT' &&
            <FormattedMessage id={subtitle}>{
              txt => <p className={style.status}>{txt}</p>
            }</FormattedMessage>}
          </section>
          <i className={classNames(style.chevron, open ? icons.iconChevronUpMedium : icons.iconChevronDownMedium)}/>
        </button>
        <section className={classNames(formStyle.formWrap, style.contentWrapper, { [style.open]: open })}>
          {/*url*/}
          <section className={classNames(formStyle.row, formStyle.singleRow)}>
            <FormattedMessage id="product.link.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <a href={product.url}
               target="_blank"
               className={style.url}>{this.buildUrl(product.url)}</a>
          </section>
          {isLoading && <Loading className={style.loading}
                                 type="circle"/>}
          {/*properties*/}
          {this.buildFirstRow()}
          {this.buildSecondRow()}
          {/*category*/}
          <section className={classNames(formStyle.row, formStyle.singleRow, style.row, style.mobile,
            { [style.disabled]: isLoading })}>
            <FormattedMessage id="product.category.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            {categoriesMap &&
            <p>{categoriesMap[`${item.itemCategoryId}`].translations[localeState.locale.toUpperCase()].name}</p>}
          </section>
          {product.detail &&
          <section className={classNames(formStyle.row, formStyle.singleRow,
            { [style.disabled]: isLoading })}>
            <FormattedMessage id="product.description.label">{
              txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
            }</FormattedMessage>
            <p>{product.detail}</p>
          </section>}
          {type === 'EDIT' && !!handleDelete &&
          <Fragment>
            <section className={classNames(formStyle.row, formStyle.dualWrap, formStyle.responsive,
              { [style.disabled]: isLoading })}>
              <article className={formStyle.col}>
                <FormattedMessage id="product.shipping.label">{
                  txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
                }</FormattedMessage>
                {shipping && handleChangeShipping && handleShippingInit && handleBlurCleave &&
                <InputPrice name="shipping"
                            value={shipping.value}
                            hasError={!!shipping.errors}
                            handleChangeCleave={(e: Event) => handleChangeShipping(e, product.number, index)}
                            handleBlurCleave={(e: Event) => handleBlurCleave(e, product.number, index)}
                            handleInit={(cleave: Cleave) => handleShippingInit(cleave, product.number, index)}
                            handleRef={handleRef}/>
                  /*<ShippingInput shipping={shipping}
                                 handleChange={(e: Event) => handleChangeShipping(e, product.number, index)}
                                 handleShippingInit={(cleave: Cleave) => handleShippingInit(cleave, product.number, index)}
                                 handleBlur={(e: Event) => handleBlurCleave(e, product.number, index)}
                                 onlineStore={onlineStore}/>*/}
              </article>
            </section>
            {!hideDelete && <section className={style.options}>
              {/*<FormattedMessage id="product.options.edit">{
                txt => <button onClick={handleEdit}>{txt}</button>
              }</FormattedMessage>*/}
              <FormattedMessage id="product.options.delete">{
                txt => <button onClick={() => handleDelete(product.number, index)}
                               className={classNames(style.delete, { [style.disabled]: isLoading })}>{txt}</button>
              }</FormattedMessage>
            </section>}
          </Fragment>}
          {(type === 'CANCELED' || !!renderContent) && <hr className={style.separator}/>}
          {/*bottom*/}
          {type === 'CANCELED' &&
          <section className={style.bottomWrapper}>
            <FormattedMessage id="product.canceled">{
              txt => <p className={style.label}>{txt}</p>
            }</FormattedMessage>
            {cancelationMessage &&
            <FormattedMessage id="product.cancelationMotive">{
              txt => <p className={style.motive}>{txt}{cancelationMessage}</p>
            }</FormattedMessage>}
          </section>}
          {renderContent && renderContent(this.props)}
          {/*{(type === 'SUCCESS' || type === 'VIEW') &&
          <section className={style.bottomWrapper}>
            {tracking &&
            <section className={classNames(formStyle.row, formStyle.singleRow)}>
              <FormattedMessage id="product.tracking.label">{
                txt => (<label className={classNames(formStyle.label, style.label)}>{txt}</label>)
              }</FormattedMessage>
              <p>{tracking.trackingNumber}</p>
            </section>}
            invoice
            <FormattedMessage id="product.invoice">{
              txt => <a className={style.invoice}
                        href={`${externalInvoice ? externalInvoice.image : ''}`}
                        target="_blank"><i className={icons.iconListLight}/>{txt}</a>
            }</FormattedMessage>
          </section>}*/}
          {showVolumeTier && this.buildVolumeBlock()}
        </section>
      </section>);
  }
}

Product.propTypes = {
  index: PropTypes.number.isRequired,
  localeState: PropTypes.object.isRequired,
  categoriesMap: PropTypes.object,
  item: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  showVolumeTier: PropTypes.bool,
  type: PropTypes.string,
  handleOpen: PropTypes.func.isRequired,
  handleChangeAmount: PropTypes.func,
  handleChangeShipping: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  hideDelete: PropTypes.bool,
  handleShippingInit: PropTypes.func,
  shipping: PropTypes.object,
  amount: PropTypes.object,
  onlineStore: PropTypes.object,
  renderContent: PropTypes.func,
  subtitle: PropTypes.string,
  userType: PropTypes.string,
  handleRef: PropTypes.func,
};

Product.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/elements/Product/Product.jsx